import { Modal } from '@/components/Elements';
import { Button } from '@/components/TSX components';
import 'rsuite/dist/rsuite.min.css';
import Input from '@/components/Input';
import { CaretRight, MagnifyingGlass, X, XCircle } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { Skeleton } from '@mui/material';
import { SourcesModalColumnList } from './SourcesModalColumnList';
import { isEmpty, isEqual } from 'lodash';
import { SearchResults } from './SearchResults';
import { SourcesModalTableList } from './SourcesModalTableList';
import { ClickAwayListener } from '@mui/material';
import { queryClient } from '@/lib/react-query';
import ToolTip from '@/components/Tooltip';

export const SourcesModal = ({
  name,
  isOpen,
  handleCloseModal,
  integrations,
  integrationsLoading,
  sources,
  setSources,
  bkpSources,
  setBkpSources,
  sourcesTableMapping,
  setSourcesTableMapping,
  bkpSourcesTableMapping,
  setBkpSourcesTableMapping,
  searchQuery,
  setSearchQuery,
  searchDebounce,
}) => {
  const [firstFetch, setFirstFetch] = useState(true);
  const [sourceId, setSourceId] = useState({});
  const [tableId, setTableId] = useState({});
  const [columns, setColumns] = useState([]);
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(true);
  const [filteredTables, setFilteredTables] = useState([]);

  const [allSources, setAllSources] = useState([]);
  const [immediateSearchQuery, setImmediateSearchQuery] = useState('');
  const [colRefetch, setColRefetch] = useState(false);
  const [tableRefetch, setTableRefetch] = useState(false);
  const [tooltipShow, setTooltipShow] = useState(false);
  const [tableColumns, setTableColumns] = useState([]);

  const [searchFocused, setSearchFocused] = useState(false);
  const [canSwitchTable, setCanSwitchTable] = useState({});
  const [canSwitchColumn, setCanSwitchColumn] = useState({});

  const [currentColPageNumber, setCurrentColPageNumber] = useState(1);

  const [canScrollCol, setCanScrollCol] = useState({ bool: false, ind: -1 });
  const [tableScrollingDone, setTableScrollingDone] = useState(false);

  const [tabIndex, setTabIndex] = useState(0);

  const tableRef = useRef(null);
  const columnRef = useRef(null);

  const handleCloseModalHandler = () => {
    handleCloseModal();
  };

  useEffect(() => {
    if (!isOpen) {
      queryClient.resetQueries('searchedTablesWithTableName');
      queryClient.resetQueries('searchedColumnsWithColumnName');
    }
  }, [isOpen]);

  function customScrollToElement(
    element,
    parent,
    duration = 1000,
    block = 'start'
  ) {
    if (!element || !parent) return;

    const startPosition = parent.scrollTop;
    const elementRect = element.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    let targetPosition;
    const elementTopRelativeToParent = elementRect.top - parentRect.top;
    if (block === 'center') {
      targetPosition =
        elementTopRelativeToParent +
        parent.scrollTop -
        (parent.clientHeight / 2 - elementRect.height / 2);
    } else if (block === 'end') {
      targetPosition =
        elementTopRelativeToParent +
        parent.scrollTop -
        (parent.clientHeight - elementRect.height);
    } else {
      targetPosition = elementTopRelativeToParent + parent.scrollTop;
    }
    const distance = targetPosition - startPosition;
    let startTime = null;
    function animation(currentTime) {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const scrollY = ease(timeElapsed, startPosition, distance, duration);
      parent.scrollTop = scrollY;

      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    }
    function ease(t, b, c, d) {
      t /= d / 2;
      if (t < 1) return (c / 2) * t * t + b;
      t--;
      return (-c / 2) * (t * (t - 2) - 1) + b;
    }
    requestAnimationFrame(animation);
  }

  useEffect(() => {
    if (
      firstFetch &&
      !integrationsLoading &&
      Array.isArray(integrations) &&
      integrations.length > 0
    ) {
      setSourceId(integrations[0]);
      integrations.forEach((source, index) => {
        let mapArr = [];
        source.tables.forEach((tab) => {
          const maps = sourcesTableMapping[tab?.id];
          if (maps !== undefined) {
            mapArr.push(sources[tab.id]);
          }
        });
        integrations[index].maps = mapArr;
      });
      setFirstFetch(false);
    }
  }, [firstFetch, integrationsLoading, integrations]);

  useEffect(() => {
    if (
      !integrationsLoading &&
      !tablesLoading &&
      Array.isArray(tables) &&
      tables.length > 0 &&
      sourceId?.id &&
      sourceId?.id.length > 0
    ) {
      const temp = structuredClone(tables);
      temp.forEach((tab, index) => {
        const maps = sources[tab.id];
        if (maps !== undefined) {
          temp[index].maps = maps;
        }
      });
      setFilteredTables(temp);
    }
  }, [sourceId, tables, tablesLoading, integrationsLoading]);

  useEffect(() => {
    Object.keys(sources).forEach((key) => {
      if (sources[key].length === 0) {
        delete sources[key];
      }
    });

    if (Array.isArray(tables) && Array.isArray(integrations)) {
      const temp = tables.filter(
        (table) => table.integrationId === sourceId?.id
      );
      temp.forEach((tab, index) => {
        const maps = sources[tab.id];
        if (maps !== undefined) {
          temp[index].maps = maps;
        } else {
          if (temp[index].maps !== undefined) {
            delete temp[index].maps;
          }
        }
      });
      setFilteredTables(temp);
      integrations.forEach((source, index) => {
        let mapArr = [];
        source.tables.forEach((tab) => {
          const maps = sourcesTableMapping[tab?.id];
          if (maps !== undefined) {
            mapArr.push(sources[tab.id]);
          }
        });
        integrations[index].maps = mapArr;
        if (mapArr.length === 0) {
          delete integrations[index].maps;
        }
      });
    }
  }, [sources, tables, integrations]);

  useEffect(() => {
    if (Array.isArray(integrations) && !integrationsLoading) {
      setAllSources(integrations);
    }
  }, [integrations, integrationsLoading]);

  const handleTableClick = (tab) => {
    const source =
      allSources[allSources.findIndex((src) => src.id === tab.integrationId)];
    setSourceId(source);
    setCanSwitchTable(tab);
    setSearchQuery('');
  };

  useEffect(() => {
    if (
      !tablesLoading &&
      Array.isArray(tables) &&
      tables.length > 0 &&
      !isEmpty(canSwitchTable)
    ) {
      const integId = tables[0].integrationId;
      if (integId === canSwitchTable.integrationId) {
        let ind = tables.findIndex((tab) => tab.id === canSwitchTable.id);
        if (ind === -1) {
          setTables((prev) => [canSwitchTable, ...prev]);
          ind = 0;
        }
        setTableId(tables[ind]);
        if (tableRef.current) {
          setTimeout(() => {
            let tabHL = tableRef.current.children[ind];
            if (tabHL) {
              // tabHL.scrollIntoView({ behavior: 'smooth', block: 'center' });
              customScrollToElement(tabHL, tableRef.current, 500, 'center');
            }
            setTimeout(() => {
              setTableScrollingDone(true);
            }, 500);
          }, 10);
        }
      }
    }
  }, [tablesLoading, tables, canSwitchTable]);

  useEffect(() => {
    if (canSwitchTable?.id === tableId?.id && !isEmpty(canSwitchTable)) {
      setCanSwitchTable({});
    }
  }, [tableId, canSwitchTable]);

  const handleColumnClick = (col) => {
    const tab = {
      description: col.tableDescription,
      disabledForGuidedAnalysis: col.disabledForGuidedAnalysis,
      id: col.tableId,
      integrationId: col.integrationId,
      integrationName: col.integrationName,
      integration_logo: col.integrationLogo,
      name: col.tableName,
      platform: col.platform,
    };
    handleTableClick(tab);
    setCanSwitchColumn(col);
  };

  useEffect(() => {
    if (
      !isEmpty(canSwitchColumn) &&
      tableId.id === canSwitchColumn.tableId &&
      tableColumns.length > 0 &&
      tableColumns[0].tableId === tableId.id
    ) {
      let ind = tableColumns.findIndex(
        (column) => column.name === canSwitchColumn.name
      );
      if (ind === -1) {
        let tempCol = {
          description: canSwitchColumn.columnDescription,
          disabledForGuidedAnalysis: canSwitchColumn.disabledForGuidedAnalysis,
          integrationId: canSwitchColumn.integrationId,
          label: canSwitchColumn.label,
          mappings: canSwitchColumn.mappings,
          name: canSwitchColumn.name,
          nonNullValuePercentage: canSwitchColumn.nonNullValuePercentage,
          platform: canSwitchColumn.platform,
          primary: canSwitchColumn.primary,
          tableId: canSwitchColumn.tableId,
          tableName: canSwitchColumn.tableName,
          tags: canSwitchColumn.tags,
          type: canSwitchColumn.type,
        };
        if (tableColumns.length > 10) {
          ind = tableColumns.length - 7;
        } else {
          ind = tableColumns.length;
        }
        setTableColumns((prev) => {
          let temp = structuredClone(prev || []);
          if (temp.length > 10) {
            return [
              ...temp.slice(0, temp.length - 7),
              tempCol,
              ...temp.slice(temp.length - 6, temp.length - 1),
            ];
          } else {
            return [...temp, tempCol];
          }
        });
      }
      setCanScrollCol({ bool: true, ind: ind });
      setCanSwitchColumn({});
    }
  }, [tableId.id, canSwitchColumn, tableColumns]);

  useEffect(() => {
    if (tableScrollingDone && tableColumns.length > 0 && canScrollCol.bool) {
      if (columnRef.current) {
        setTimeout(() => {
          let colHL = columnRef.current.children[canScrollCol.ind];
          if (colHL) {
            // colHL.scrollIntoView({ behavior: 'smooth', block: 'center' });
            customScrollToElement(colHL, columnRef.current, 600, 'center');
          }
          colHL = colHL.querySelector('.col-el');
          colHL.classList.add('bg-pv-primary-primary-50');
          const texts = colHL.querySelectorAll('.col-txt');
          const textArray = Array.from(texts);
          textArray.forEach((txt) =>
            txt.classList.add('text-pv-primary-primary-500')
          );
          setTimeout(() => {
            colHL.classList.remove('bg-pv-primary-primary-50');
            textArray.forEach((txt) =>
              txt.classList.remove('text-pv-primary-primary-500')
            );
          }, 4000);
          setSearchQuery('');
          setTableScrollingDone(false);
        }, 10);
        setCanScrollCol({ bool: false, ind: -1 });
      }
    }
  }, [tableColumns, canScrollCol, tableScrollingDone]);

  let newCols = [];

  if (Object.keys(sources).length > 0) {
    Object.keys(sources).forEach((src) => {
      if (bkpSources[src] === undefined) {
        newCols.push(...sources[src]);
      } else {
        sources[src].forEach((s) => {
          if (
            bkpSources[src].findIndex((bkpS) => bkpS.name === s.name) === -1
          ) {
            newCols.push(s);
          }
        });
      }
    });
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        const temp = structuredClone(bkpSources || {});
        setSources(temp);
        handleCloseModalHandler();
        setSearchQuery('');
        setImmediateSearchQuery('');
      }}
      topStripClassName="hidden"
      topBorderClassname="border-t-5 border-pv-primary-primary-500"
      className="relative w-fit"
      showHeader={false}
    >
      <div
        className="flex flex-col justify-between"
        style={{
          zIndex: 0,
        }}
      >
        <div
          className="flex w-full justify-between p-4 items-center"
          style={{
            zIndex: 1,
          }}
        >
          <span className="text-lg font-medium">Add {name}</span>
          <div className="flex items-center gap-2 relative">
            <ClickAwayListener
              onClickAway={() => {
                setSearchQuery('');
              }}
            >
              <div
                className={` bg-white border focus-within:border-pv-primary-primary-500 hover:border-pv-primary-primary-500 rounded-lg absolute ${
                  searchQuery.length > 0
                    ? `border-pv-primary-primary-500`
                    : `border-pv-neutral-grey-200 `
                }`}
                style={{
                  right: '24px',
                  top: '-7px',
                  boxShadow:
                    searchQuery.length > 0 ? `0 0 25px -15px black` : ``,
                  width: '500px',
                }}
              >
                <Input
                  leftElem={
                    <MagnifyingGlass
                      size={16}
                      className={`${
                        searchFocused
                          ? 'text-pv-neutral-grey-500'
                          : 'text-pv-neutral-grey-300'
                      }`}
                    />
                  }
                  rightElem={
                    immediateSearchQuery === '' ? null : (
                      <XCircle
                        size={16}
                        className="mr-2 cursor-pointer text-pv-neutral-grey-300 hover:text-pv-neutral-grey-900"
                        onClick={() => {
                          setSearchQuery('');
                          setImmediateSearchQuery('');
                        }}
                      />
                    )
                  }
                  className={{
                    input: {
                      wrapper: 'pr-0 pl-2 py-1.5 m-0 w-full border-none',
                      root: 'text-xs',
                    },
                  }}
                  value={immediateSearchQuery}
                  onChange={(e) => {
                    searchDebounce(e);
                    setImmediateSearchQuery(e.target.value);
                  }}
                  iconClassName="left-0 pl-4"
                  placeholder={`Search Tables/Columns`}
                  onFocus={() => {
                    setSearchQuery(immediateSearchQuery);
                    setSearchFocused(true);
                  }}
                  onBlur={() => {
                    setSearchFocused(false);
                  }}
                />
                {searchQuery.length > 0 &&
                  Array.isArray(tables) &&
                  Array.isArray(tableColumns) &&
                  Array.isArray(allSources) &&
                  tableId.name && (
                    <SearchResults
                      searchQuery={searchQuery}
                      tableId={tableId}
                      handleTableClick={handleTableClick}
                      handleColumnClick={handleColumnClick}
                      tabIndex={tabIndex}
                      setTabIndex={setTabIndex}
                      setSearchQuery={setSearchQuery}
                      setImmediateSearchQuery={setImmediateSearchQuery}
                      immediateSearchQuery={immediateSearchQuery}
                    />
                  )}
              </div>
            </ClickAwayListener>
            <X
              size={16}
              className="cursor-pointer"
              onClick={() => {
                const temp = structuredClone(bkpSources || {});
                setSources(temp);
                handleCloseModalHandler();
                setSearchQuery('');
                setImmediateSearchQuery('');
              }}
            />
          </div>
        </div>
        <div
          className="px-4 flex gap-3"
          style={{
            height: '524px',
            zIndex: 0,
          }}
        >
          <div className="flex flex-col gap-2">
            <span className="text-xs text-pv-text-secondary-text">
              Select Integration
            </span>
            <div
              className="overflow-y-auto border border-pv-neutral-grey-100 rounded-lg py-2.5 px-3"
              style={{
                height: '500px',
                width: '350px',
              }}
            >
              {allSources?.length > 0 ? (
                allSources.map((source, ind) => {
                  return (
                    <div
                      className="flex flex-col w-full"
                      onClick={() => {
                        setSourceId(source);
                      }}
                    >
                      {ind > 0 && (
                        <span
                          className="w-full bg-pv-neutral-grey-100 px-2"
                          style={{
                            height: '1px',
                          }}
                        ></span>
                      )}
                      <div
                        className={`flex items-center px-4 py-3 border-l-3 border-transparent cursor-pointer hover:bg-pv-primary-primary-50 justify-between ${
                          sourceId?.id === source.id
                            ? 'border-pv-primary-primary-500'
                            : ''
                        }`}
                        key={ind}
                      >
                        <div className="flex gap-2 items-center">
                          <img
                            src={source.logo}
                            style={{
                              height: '21px',
                              aspectRatio: 1,
                            }}
                          />
                          <span
                            className={`max-w-52 overflow-ellipsis whitespace-nowrap overflow-hidden ${
                              sourceId?.id === source.id ? `font-medium` : ``
                            }`}
                          >
                            {source.datasource}
                          </span>
                        </div>
                        <div className="flex gap-2 items-center">
                          {source.maps?.length > 0 && (
                            <span className="bg-pv-primary-primary-500 text-white text-1xs font-medium px-1.5 py-0.5 rounded-md">
                              {source.maps.flat().length}
                            </span>
                          )}
                          <CaretRight />
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : allSources.length === 0 && !integrationsLoading ? (
                <span className="w-full text-pv-text-secondary-text text-xs">
                  No Sources Found
                </span>
              ) : (
                <div className="w-full flex flex-col gap-5 p-2">
                  {Array.from({ length: 10 }).map((_, index) => (
                    <div className="flex gap-2 py-1" key={index}>
                      <Skeleton
                        variant="rounded"
                        width={20}
                        height={20}
                        sx={{ bgcolor: '#EEF0F7' }}
                      />
                      <Skeleton
                        variant="rounded"
                        width={index % 2 === 1 ? '30%' : '60%'}
                        height={20}
                        sx={{ bgcolor: '#EEF0F7' }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center">
              <ToolTip
                placement="top"
                title={
                  allSources.length === 0
                    ? `Select Tables`
                    : `Select Tables for ${sourceId?.datasource}`
                }
                tooltipActive={tooltipShow}
                displayTooltipOnOverflow
              >
                <span
                  className="text-xs text-pv-text-secondary-text overflow-ellipsis overflow-hidden whitespace-nowrap"
                  style={{
                    maxWidth: '350px',
                  }}
                >
                  {allSources.length === 0
                    ? `Select Tables`
                    : `Select Tables for ${sourceId?.datasource}`}
                </span>
              </ToolTip>
            </div>

            <div
              className="overflow-y-auto border border-pv-neutral-grey-100 rounded-lg py-2.5 px-3"
              style={{
                height: '500px',
                width: '350px',
              }}
              onScroll={(e) => {
                setTooltipShow(false);
                if (e.target.scrollTop >= e.target.scrollHeight - 1500) {
                  setTableRefetch(true);
                }
              }}
              ref={tableRef}
            >
              {integrationsLoading ? (
                <div className="w-full flex flex-col gap-5 p-2">
                  {Array.from({ length: 10 }).map((_, index) => (
                    <div className="flex gap-2 py-1" key={index}>
                      <Skeleton
                        variant="rounded"
                        width={index % 2 === 1 ? '35%' : '65%'}
                        height={20}
                        sx={{ bgcolor: '#EEF0F7' }}
                      />
                    </div>
                  ))}
                </div>
              ) : sourceId?.id && sourceId?.id?.length > 0 ? (
                <SourcesModalTableList
                  sourceId={sourceId}
                  tableId={tableId}
                  setTableId={setTableId}
                  tableRefetch={tableRefetch}
                  setTableRefetch={setTableRefetch}
                  tables={tables}
                  setTables={setTables}
                  searchQuery={searchQuery}
                  tablesLoading={tablesLoading}
                  setTablesLoading={setTablesLoading}
                  integrationsLoading={integrationsLoading}
                  setCurrentColPageNumber={setCurrentColPageNumber}
                  tooltipShow={tooltipShow}
                  setTooltipShow={setTooltipShow}
                />
              ) : (
                <span className="w-full text-pv-text-secondary-text text-xs">
                  No Tables Found
                </span>
              )}
            </div>
          </div>
          <div
            className="flex flex-col gap-2"
            style={{
              maxWidth: '350px',
            }}
          >
            <div className="flex items-center">
              <ToolTip
                placement="top"
                title={
                  filteredTables.length === 0
                    ? `Select Columns`
                    : `Select Columns for ${tableId?.name}`
                }
                tooltipActive={tooltipShow}
                displayTooltipOnOverflow
              >
                <span
                  className="text-xs text-pv-text-secondary-text w-full overflow-hidden overflow-ellipsis whitespace-nowrap"
                  style={{
                    maxWidth: '350px',
                  }}
                >
                  {filteredTables.length === 0
                    ? `Select Columns`
                    : `Select Columns for ${tableId?.name}`}
                </span>
              </ToolTip>
            </div>
            <div
              className="overflow-y-auto border border-pv-neutral-grey-100 rounded-lg py-2.5 px-3"
              style={{
                height: '500px',
                width: '350px',
              }}
              onScroll={(e) => {
                setTooltipShow(false);
                if (e.target.scrollTop >= e.target.scrollHeight - 1500) {
                  setColRefetch(true);
                }
              }}
              ref={columnRef}
            >
              {(tablesLoading && !tableRefetch) || integrationsLoading ? (
                <div className="w-full flex flex-col gap-5 p-2">
                  {Array.from({ length: 10 }).map((_, index) => (
                    <div className="flex gap-2 py-1" key={index}>
                      <Skeleton
                        variant="rounded"
                        width={index % 2 === 1 ? '35%' : '65%'}
                        height={20}
                        sx={{ bgcolor: '#EEF0F7' }}
                      />
                    </div>
                  ))}
                </div>
              ) : tableId?.id && tableId?.id?.length > 0 ? (
                <SourcesModalColumnList
                  tableId={tableId}
                  sources={sources}
                  setSources={setSources}
                  columns={columns}
                  setColumns={setColumns}
                  sourcesTableMapping={sourcesTableMapping}
                  setSourcesTableMapping={setSourcesTableMapping}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  tablesLoading={tablesLoading}
                  integrationsLoading={integrationsLoading}
                  colRefetch={colRefetch}
                  setColRefetch={setColRefetch}
                  tooltipShow={tooltipShow}
                  setTooltipShow={setTooltipShow}
                  tableColumns={tableColumns}
                  setTableColumns={setTableColumns}
                  tableRefetch={tableRefetch}
                  currentColPageNumber={currentColPageNumber}
                  setCurrentColPageNumber={setCurrentColPageNumber}
                  columnRef={columnRef}
                  immediateSearchQuery={immediateSearchQuery}
                />
              ) : (
                <span className="w-full text-pv-text-secondary-text text-xs">
                  No Columns Found
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="border-t border-pv-neutral-grey-100 mt-2 w-full">
          <div className="flex justify-between items-center p-4">
            <Button
              btnColor="ghost"
              onClick={() => {
                const temp = structuredClone(bkpSources || {});
                setSources(temp);
                const tempMapping = structuredClone(
                  bkpSourcesTableMapping || {}
                );
                setSourcesTableMapping(tempMapping);
                handleCloseModalHandler();
                setSearchQuery('');
                setImmediateSearchQuery('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const temp = structuredClone(sources || {});
                setBkpSources(temp);
                const tempMapping = structuredClone(sourcesTableMapping || {});
                setBkpSourcesTableMapping(tempMapping);
                handleCloseModalHandler();
              }}
              disabled={isEqual(sources, bkpSources)}
            >
              <span className="gap-1 flex">
                <span>Add {name}</span>
                {newCols.length > 0 && <span>({newCols.length})</span>}
              </span>
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
